import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Search, LayoutTemplate, Star } from 'lucide-react';
import { BUILTIN_TEMPLATES, CATEGORY_LABELS } from '@/lib/emailTemplates';
import { emailTemplatesAPI } from '@/lib/api';
import { toast } from 'sonner';

// Small HTML preview rendered in a scaled iframe
const TemplatePreview = ({ html }) => {
  const iframeRef = React.useRef(null);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open();
    doc.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/>
      <style>body{margin:0;padding:0;background:#fff;}</style>
      </head><body>${html}</body></html>`);
    doc.close();
  }, [html]);

  return (
    <div style={{ width: '100%', height: '100%', overflow: 'hidden', borderRadius: 8, background: '#FBF8F2', border: '1px solid #E3DDD1' }}>
      <iframe
        ref={iframeRef}
        title="Template preview"
        style={{
          width: '600px',
          height: '900px',
          border: 'none',
          transform: 'scale(0.38)',
          transformOrigin: 'top left',
          pointerEvents: 'none',
        }}
      />
    </div>
  );
};

export const TemplatePickerDialog = ({ open, onClose, onSelect }) => {
  const [search, setSearch] = useState('');
  const [userTemplates, setUserTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected(null);
    setSearch('');
    setLoading(true);
    emailTemplatesAPI.list()
      .then(r => setUserTemplates(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open]);

  const allTemplates = [...BUILTIN_TEMPLATES, ...userTemplates];

  const categories = ['all', ...new Set(allTemplates.map(t => t.category))];

  const filtered = allTemplates.filter(t => {
    const matchesCategory = activeCategory === 'all' || t.category === activeCategory;
    const matchesSearch = !search || t.name.toLowerCase().includes(search.toLowerCase()) || t.description?.toLowerCase().includes(search.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleUse = () => {
    if (!selected) return;
    onSelect(selected.html_body);
    onClose();
    toast.success(`Template "${selected.name}" applied`);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl w-full p-0 gap-0 overflow-hidden" style={{ height: '80vh' }}>
        <DialogHeader className="px-6 py-4 border-b border-[#E3DDD1] flex-shrink-0">
          <DialogTitle className="text-[#1E2A33] font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <LayoutTemplate className="w-5 h-5 text-[#A62B1F]" />
            Choose a Template
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-1 overflow-hidden" style={{ height: 'calc(80vh - 130px)' }}>
          {/* Left panel — template list */}
          <div className="w-72 flex-shrink-0 border-r border-[#E3DDD1] flex flex-col">
            {/* Search */}
            <div className="p-3 border-b border-[#E3DDD1]">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#9AA3AB]" />
                <Input
                  placeholder="Search templates..."
                  className="pl-8 text-sm h-8"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
            </div>

            {/* Category pills */}
            <div className="flex gap-1.5 px-3 py-2 border-b border-[#E3DDD1] flex-wrap">
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                    activeCategory === cat
                      ? 'bg-[#A62B1F] text-white'
                      : 'bg-[#F3EFE7] text-[#5C6670] hover:bg-[#E3DDD1]'
                  }`}
                >
                  {cat === 'all' ? 'All' : CATEGORY_LABELS[cat] || cat}
                </button>
              ))}
            </div>

            {/* Template list */}
            <div className="flex-1 overflow-y-auto p-2">
              {loading && (
                <p className="text-xs text-[#9AA3AB] text-center py-4">Loading…</p>
              )}
              {!loading && filtered.length === 0 && (
                <p className="text-xs text-[#9AA3AB] text-center py-4">No templates found</p>
              )}
              {filtered.map(t => (
                <button
                  key={t._id}
                  onClick={() => setSelected(t)}
                  className={`w-full text-left p-3 rounded-lg mb-1 transition-colors border ${
                    selected?._id === t._id
                      ? 'bg-[#F7ECEA] border-[#E7C3BC]'
                      : 'bg-white border-transparent hover:bg-[#FBF8F2] hover:border-[#E3DDD1]'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        {t.is_builtin && (
                          <Star className="w-3 h-3 text-[#A62B1F] flex-shrink-0" />
                        )}
                        <p className="text-sm font-medium text-[#1E2A33] truncate">{t.name}</p>
                      </div>
                      <p className="text-xs text-[#5C6670] line-clamp-2">{t.description || CATEGORY_LABELS[t.category]}</p>
                    </div>
                  </div>
                  <div className="mt-2">
                    <Badge
                      variant="secondary"
                      className="text-[10px] py-0 px-1.5"
                      style={{ backgroundColor: t.is_builtin ? '#F7ECEA' : '#F3EFE7', color: t.is_builtin ? '#A62B1F' : '#5C6670' }}
                    >
                      {t.is_builtin ? 'Built-in' : 'Saved'}
                    </Badge>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Right panel — preview */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {selected ? (
              <>
                <div className="flex items-center justify-between px-5 py-3 border-b border-[#E3DDD1] flex-shrink-0">
                  <div>
                    <p className="font-semibold text-[#1E2A33] text-sm">{selected.name}</p>
                    <p className="text-xs text-[#5C6670]">{selected.description}</p>
                  </div>
                  <Button
                    onClick={handleUse}
                    style={{ backgroundColor: '#A62B1F', color: 'white' }}
                    className="text-sm"
                  >
                    Use This Template
                  </Button>
                </div>
                <div className="flex-1 overflow-auto p-5 bg-[#FBF8F2]">
                  <div style={{ height: '342px', maxWidth: '228px', margin: '0 auto' }}>
                    <TemplatePreview html={selected.html_body} />
                  </div>
                  <p className="text-xs text-[#9AA3AB] text-center mt-3">Preview (scaled). Actual email will be full size.</p>
                </div>
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
                <LayoutTemplate className="w-12 h-12 text-[#E7C3BC] mb-4" />
                <p className="text-[#5C6670] font-medium mb-1">Select a template to preview</p>
                <p className="text-xs text-[#9AA3AB]">Click any template on the left to see how it looks</p>
              </div>
            )}
          </div>
        </div>

        {/* Bottom bar */}
        <div className="px-6 py-3 border-t border-[#E3DDD1] flex items-center justify-between flex-shrink-0">
          <p className="text-xs text-[#9AA3AB]">{filtered.length} template{filtered.length !== 1 ? 's' : ''}</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
            <Button
              size="sm"
              disabled={!selected}
              onClick={handleUse}
              style={selected ? { backgroundColor: '#A62B1F', color: 'white' } : {}}
            >
              Use Template
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
